<template>
  <v-card class="px-8 pt-8">
    <v-row justify="end" no-gutters>
      <v-col>
        <h1>{{ $t("faqModulesMsg") }}</h1>
      </v-col>
      <v-col cols="auto">
        <v-btn
          depressed
          class="text-none"
          color="primary"
          @click="$emit('createFaqModuleClicked')"
        >
          {{ $t("createFaqModuleMsg") }}
          <v-icon right> mdi-plus-circle-outline </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-card-text class="px-2 py-2 mt-4" elevation="0">
      <LoadingArea :loading="tableLoading">
        <v-row>
          <v-col cols="12">
            <v-simple-table class="faq-modules-table">
              <template #default>
                <thead>
                  <tr class="secondary">
                    <th class="text-left white--text">
                      <div class="d-flex align-center">
                        <span>{{ $t("changeOrderMsg") }}</span>
                        <v-tooltip top>
                          <template #activator="{ on, attrs }">
                            <v-icon
                              color="white"
                              class="ml-2"
                              v-on="on"
                              v-bind="attrs"
                            >
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <span>
                            {{ $t("faqChangeOrderHelpMsg") }}
                          </span>
                        </v-tooltip>
                      </div>
                    </th>
                    <th class="text-left white--text">{{ $t("orderMsg") }}</th>
                    <th class="text-left white--text">{{ $t("moduleMsg") }}</th>
                    <th class="text-right white--text">
                      {{ $t("actionsMsg") }}
                    </th>
                  </tr>
                </thead>
                <Draggable
                  v-model="innerFaqModules"
                  draggable=".draggable"
                  tag="tbody"
                >
                  <template v-if="faqModulesNotEmpty">
                    <tr
                      v-for="(faqModule, index) in innerFaqModules"
                      :key="faqModule.id"
                      class="draggable"
                    >
                      <td align="center">
                        <div class="d-flex align-center">
                          <div class="d-flex flex-column">
                            <v-btn
                              v-if="index !== 0"
                              x-small
                              icon
                              @click="
                                onMoveFaqModuleClicked(index, directions.up)
                              "
                            >
                              <v-icon>mdi-arrow-up</v-icon>
                            </v-btn>
                            <v-btn
                              v-if="!isLastItemInList(index, faqModules)"
                              :class="{
                                'mt-2': index !== 0,
                              }"
                              x-small
                              icon
                              @click="
                                onMoveFaqModuleClicked(index, directions.down)
                              "
                            >
                              <v-icon>mdi-arrow-down</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </td>
                      <td>
                        {{ index + 1 }}
                      </td>
                      <td v-html="faqModule.ui_content" class="pt-4"></td>
                      <td align="right">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              @click="$emit('editFaqModuleClicked', faqModule)"
                              class="grey lighten-3 mr-1 success--text"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small> mdi-pencil </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("editMsg") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              @click="
                                $emit('deleteFaqModuleClicked', faqModule)
                              "
                              class="grey lighten-3 mr-1 error--text"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small> mdi-delete </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("deleteMsg") }}</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr>
                      <td colspan="100%" align="center">
                        {{ tableLoading ? "Loading" : "No FAQ Modules" }}
                      </td>
                    </tr>
                  </template>
                </Draggable>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </LoadingArea>
    </v-card-text>
  </v-card>
</template>

<script>
import LoadingArea from "@/components/Utils/LoadingArea.vue";
import Draggable from "vuedraggable";

export default {
  components: {
    LoadingArea,
    Draggable,
  },
  props: {
    faqModules: {
      type: Array,
      default: null,
    },
    fetchingFaqModules: {
      type: Boolean,
      default: false,
    },
    faqModulesNotEmpty: {
      type: Boolean,
      default: false,
    },
    savingFaqModule: {
      type: Boolean,
      default: false,
    },
    deletingFaqModule: {
      type: Boolean,
      default: false,
    },
    updatingFaqModules: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    directions: {
      up: "up",
      down: "down",
    },
  }),
  computed: {
    innerFaqModules: {
      get() {
        return this.faqModules;
      },
      set(newVal) {
        this.$emit("update:faqModules", newVal);
      },
    },
    tableLoading() {
      return (
        this.fetchingFaqModules ||
        this.savingFaqModule ||
        this.deletingFaqModule ||
        this.updatingFaqModules
      );
    },
  },
  methods: {
    isLastItemInList(index, list) {
      return index + 1 === list.length;
    },
    onMoveFaqModuleClicked(index, direction) {
      let firstItem = null;
      let secondItem = null;
      let startIndex = null;
      if (direction === this.directions.up) {
        const isFirstItem = index === 0;
        if (isFirstItem) {
          return;
        }
        startIndex = index - 1;
        firstItem = this.faqModules[index];
        secondItem = this.faqModules[index - 1];
      } else if (direction === this.directions.down) {
        if (this.isLastItemInList(index, this.faqModules)) {
          return;
        }
        startIndex = index;
        firstItem = this.faqModules[index + 1];
        secondItem = this.faqModules[index];
      }
      const arrToSplice = [...this.innerFaqModules];
      arrToSplice.splice(startIndex, 2, firstItem, secondItem);
      this.innerFaqModules = arrToSplice;
    },
  },
};
</script>

<style lang="scss" scoped>
// ::v-deep .faq-modules-table {
//     & p {
//         margin-bottom: 0;
//     }
// }
</style>
